import { Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import { ROUTER_NAVIGATION, RouterNavigationAction } from '@ngrx/router-store';
import { RouterStateUrl } from '../../../core/router/router.state.interface';
import * as paramsActions from '../url-params.actions';
import * as appActions from '../../../core/actions/app.actions';
import { UrlParams } from '../model/url-params.model';
import { Store } from '@ngrx/store';
import { selectUrlParams } from '../url-params.selector';
import * as RouterActions from '../../../core/actions/router.actions';
import { ConfigLoadedGuard } from '../../config/config.guard';
import { Params } from '@angular/router';
import { AuthenticationService } from '../authentication.service';
import { Observable } from 'rxjs/Observable';
import { UrlParamsState } from '../url-params.state';
import { parseHash } from '../helper/hash-parser';
import { AuthenticationResponse } from '../authentication-response.model';
import * as authActions from '../actions/authentication.actions';
import { AuthParams } from '../actions/authentication.actions';
import { selectAppReady } from '../../../core/core.selector';
import { LoggerService } from '../../../core/services/logger.service';
import { ModuleRoutingConstants } from '../../../core/helpers/module-routing.constants';
import { AuthenticationErrorResponse } from '../authentication-error-response.model';
import { HttpErrorResponse } from '@angular/common/http';
import { RedirectAction } from '../../../core/actions/logout.actions';
import { AuthenticationOidcService } from '../authentication-oidc.service';
import { Globals } from '../../../app.globals';
import { Base64 } from 'js-base64';


import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/withLatestFrom';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import { of } from 'rxjs/observable/of';

@Injectable()
export class UrlParamsEffects {

    /**
     * Side effect to detect redirect to app from MCI (S24 / B24).
     *
     *
     * MCI Integration example:
     * http://uat1-cdnext.csint.cz/applications/selfservice?channelId=Servis24&lang=cs&demo=N#IB-Hash-v2=xxx
     *
     * MEP parameters description:
     * https://docs.google.com/spreadsheets/d/1cbuoEzzuo5OndV2ar3vQjUGSBOdzl68XZidJdLwIgkY/edit#gid=1439827657
     *
     */
    @Effect() fromMciTriggerAuthRequest$ = this.actions$
        .ofType(ROUTER_NAVIGATION)
        .filter((action: RouterNavigationAction<RouterStateUrl>) => this.isMCIRedirect(action.payload.routerState.queryParams))
        .combineLatest(this.configGuard.waitForConfigToLoad(), a => a)
        .map((action: RouterNavigationAction<RouterStateUrl>) => {
            // parse IB hash from url fragment
            let hashParams = parseHash(action.payload.routerState.url);

            if (!hashParams['IB-Hash-v2']) {
                return this.to401();
            }
            return new authActions.AuthenticationRequestAction(new AuthParams(action.payload.routerState.queryParams, hashParams));
        });


    @Effect() ssoAuthenticate$ = this.actions$
        .ofType(authActions.AUTHENTICATION_REQUEST)
        .switchMap((action: authActions.AuthenticationRequestAction) => this.authenticate(
                action.payload.queryParams.channelId,
                action.payload.queryParams.lang,
                action.payload.hashParams['IB-Hash-v2']),
            (action: authActions.AuthenticationRequestAction, response: AuthenticationResponse | HttpErrorResponse) => {
                this.logger.log('ssoAuthenticationResponse$: ', response);

                if (this.isAuthenticationResponse(response)) { // AuthenticationResponse
                    return new authActions.AuthenticationSuccessAction(<UrlParams>{
                        access_token: response.access_token,
                        lang: action.payload.queryParams.lang,
                        channelId: action.payload.queryParams.channelId
                    });

                } else { // HttpErrorResponse
                    let authErrorResponse: AuthenticationErrorResponse = response.error;
                    if (authErrorResponse.error_uri) {
                        return new RedirectAction(authErrorResponse.error_uri);
                    } else {
                        return new authActions.AuthenticationErrorAction(authErrorResponse.error_description);
                    }
                }
            });

    @Effect() ssoAuthenticationSuccess$ = this.actions$
        .ofType(authActions.AUTHENTICATION_SUCCESS)
        .map((action: authActions.AuthenticationSuccessAction) => new paramsActions.ChangeUrlParamsAction(action.payload));


    @Effect() ssoAuthenticationError$ = this.actions$
        .ofType(authActions.AUTHENTICATION_ERROR)
        .mergeMap((action: authActions.AuthenticationErrorAction) => {
            this.logger.log('ssoAuthenticationError$: ', action.payload);
            return [
                new appActions.AppReadyAction(),
                this.to401()
            ];
        });

    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Side effect to detect redirect to app from George.
     *
     * George constructs redirect URL as: `[ssf_url]#access_token=[new_token_from_step_1]&lang=[lang]&state=[state]`
     */
    @Effect() fromGeorge$ = this.actions$
        .ofType(ROUTER_NAVIGATION)
        .combineLatest(this.configGuard.waitForConfigToLoad(), a => a)
        .filter((action: RouterNavigationAction<RouterStateUrl>) =>
            this.isGeorgeRedirect(action.payload.routerState.queryParams, parseHash(action.payload.routerState.url)))
        .map((action: RouterNavigationAction<RouterStateUrl>) => {
            this.logger.log('fromGeorge$: action: ', action);
            const hashParams = parseHash(action.payload.routerState.url);
            return new paramsActions.ChangeUrlParamsAction(<UrlParams>{
                lang: hashParams.lang,
                access_token: hashParams.access_token,
                ...(hashParams.state ? {state: JSON.parse(Base64.decode(hashParams.state))} : {}),
            });
        });

    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Side effect to detect redirect to app from George.
     *
     * George constructs redirect URL as: `[ssf_url]#access_token=[new_token_from_step_1]&lang=[lang]&state=[state]`
     */
    @Effect() fromGeorgeOidc$ = this.actions$
        .ofType(ROUTER_NAVIGATION)
        .combineLatest(this.configGuard.waitForConfigToLoad(), a => a)
        .filter((action: RouterNavigationAction<RouterStateUrl>) =>
            this.isGeorgeOidcRedirect(action.payload.routerState.queryParams, parseHash(action.payload.routerState.url)))
        .switchMap((action: RouterNavigationAction<RouterStateUrl>) => {
            const hashParams = parseHash(action.payload.routerState.url);
            return this.authOidc.login({
                idToken: hashParams.id_token,
                code: hashParams.code,
                state: hashParams.state,
                lang: hashParams.lang
            })
                .catch(() => {
                    return of(null);
                })
                .map(token => {
                    if (token) {
                        console.log('token receive');
                        return new paramsActions.ChangeUrlParamsAction(<UrlParams>{
                            lang: hashParams.lang || 'cs',
                            access_token: token,
                            oidc: true,
                            ...(hashParams.state ? {state: JSON.parse(Base64.decode(hashParams.state))} : {}),
                        });
                    } else {
                        this.logger.error('[UrlParamsEffect] Failed to obtain oidc access token');
                        return new authActions.AuthenticationErrorAction('oidc_login_failed');
                    }
                });
        });

    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Side effect to detect redirect to app from Cps over ADFS.
     *
     * Cps constructs redirect URL similarly to this: `[ssf_url]#access_token=adfs_9d858ddp-9578-8d95-d598-15dergfd8fer
     * &token_type=bearer&expires_in=3600&cluid=1997-08-25-20.33.50.598756
     *
     * Documented in this file: https://drive.google.com/file/d/1jlU80bQroGefUkzceYJdKMsOhirNGWw9/view
     */
    @Effect() fromCps$ = this.actions$
        .ofType(ROUTER_NAVIGATION)
        .combineLatest(this.configGuard.waitForConfigToLoad(), a => a)
        .filter((action: RouterNavigationAction<RouterStateUrl>) =>
            this.isCpsRedirect(action.payload.routerState.queryParams))
        .map((action: RouterNavigationAction<RouterStateUrl>) => {
            this.logger.log('fromCps$: action: ', action);
            const hashParams = parseHash(action.payload.routerState.url);
            return new paramsActions.ChangeUrlParamsAction(<UrlParams>{
                lang: 'cs',
                access_token: hashParams.access_token,
                cluid: action.payload.routerState.queryParams.cluid
            });
        });

    // -----------------------------------------------------------------------------------------------------------------

    @Effect() appReady$ = this.actions$
        .ofType(paramsActions.PARAMS_CHANGED)
        .mergeMap(() => {

            this.logger.log('appReady$');

            return [
                new appActions.AppReadyAction(),
                new RouterActions.Go({
                    path: ModuleRoutingConstants.BANK_IDENTITY_ROUTER
                })];
        });

    // -----------------------------------------------------------------------------------------------------------------


    /**
     * Effect to check if there is access token (AT) available:
     * - either AT is already in application redux store
     * - or there is a URL parameter present indicating that this ROUTER_NAVIGATION is redirect from MCI or George -
     * in this case the token is not ready yet
     */
    @Effect() checkLoggedIn$ = this.actions$
        .ofType(ROUTER_NAVIGATION)
        .filter((action: RouterNavigationAction) =>
            (action.payload.routerState.url.indexOf('/401') === -1)
            && (action.payload.routerState.url.indexOf('/expired') === -1)
            && (action.payload.routerState.url.indexOf('/logout') === -1)
            && (action.payload.routerState.url.indexOf('/adfs') === -1))
        .withLatestFrom(this.store.select(selectUrlParams), this.store.select(selectAppReady))
        .mergeMap(([action, storeParams, appReady]: ([RouterNavigationAction<RouterStateUrl>, UrlParams, boolean])) => {
            let queryParams = action.payload.routerState.queryParams;
            let url = action.payload.routerState.url;
            let accessToken = !!storeParams && !!storeParams.access_token;  // no access token in store
            let hashParams = parseHash(url);
            let isRedirectToApp = this.isRedirect(queryParams, hashParams);

            this.logger.log(`checkLoggedIn$: accessToken: `, accessToken, 'isRedirectToApp: ', isRedirectToApp, 'url: ', url);

            if (this.isCpsRedirect(queryParams)) {
                this.logger.log('checkLoggedIn$: isCpsRedirect');

                if (!!hashParams && !!hashParams.error) {
                    this.logger.error('checkLoggedIn$: received error from ADFS');
                    return Observable.of(this.to401());
                }

                // is redirect from CPS or ADFS (there is 'cluid' in url query params)
                let parsedToken = this.parseJwt(hashParams.access_token);

                if (!!hashParams && !!hashParams.access_token
                    && !!parsedToken.cluid && parsedToken.cluid === queryParams.cluid) {
                    // url hash params contains 'access_token'
                    // and this token contains on-behalf-of 'cluid'
                    // and cluid in token is the same as cluid in query params
                    sessionStorage.removeItem('queryCluid');
                    sessionStorage.removeItem('accessToken');
                    sessionStorage.removeItem('userId');
                    // setting role of cps user
                    let roles = parsedToken.roles;
                    if (roles) {
                        sessionStorage.setItem('roleSspAutenticationUsersRW', roles.includes(Globals.roleSspAutenticationUsersRW));
                    }
                    // app is ready
                    return Observable.of(new appActions.AppReadyAction())
                        .combineLatest(this.configGuard.waitForConfigToLoad(), a => a);
                } else {
                    // url hash params is missing 'access_token'
                    // or this token does not contain on-behalf-of 'cluid'
                    // or cluid in token is different from cluid in query params
                    sessionStorage.setItem('queryCluid', queryParams.cluid);
                    sessionStorage.setItem('accessToken', hashParams.access_token ? hashParams.access_token : null);
                    sessionStorage.setItem('userId', parsedToken.user_id);
                    // go to AdfsRedirectComponent
                    return Observable.of(this.toAdfsRedirect());
                }
            } else if (isRedirectToApp || appReady) {
                this.logger.log('checkLoggedIn$: do nothing');
                // is redirect to app - handled by one of effects from above; or app already ready then nothing
                return Observable.empty();
            } else if (!isRedirectToApp && accessToken) {
                this.logger.log('checkLoggedIn$: AppReadyAction');
                // no url params but token is available (from local storage); F5 / refresh page
                return Observable.of(new appActions.AppReadyAction())
                    .combineLatest(this.configGuard.waitForConfigToLoad(), a => a);
            } else if (!accessToken) {
                this.logger.log('checkLoggedIn$: 401');
                // no token - 401
                return Observable.of(this.to401());
            }

        });

    // -----------------------------------------------------------------------------------------------------------------
    private isAuthenticationResponse(response: AuthenticationResponse | AuthenticationErrorResponse): response is AuthenticationResponse {
        return (<AuthenticationResponse>response).access_token !== undefined;
    }

    private to401() {
        this.logger.log('to401()');

        return new RouterActions.Go({
            path: ['401']
        });
    }

    private toAdfsRedirect() {
        this.logger.log('toAdfsRedirect()');

        return new RouterActions.Go({
            path: ['adfs']
        });
    }

    parseJwt(token) {
        if (token != null) {
            let base64Url = token.split('.')[1];
            let base64 = base64Url.replace('-', '+').replace('_', '/');
            return JSON.parse(Base64.decode(base64));
        } else {
            return {
                exp: -1
            };
        }
    }

    // -----------------------------------------------------------------------------------------------------------------

    private authenticate = (channelId: string, languageId: string, iBhash: string) =>
        this.auth.authenticate(channelId, languageId, iBhash)
            .map(payload => payload)
            .catch((error) => Observable.of(error));

    private isRedirect(queryParams: Params, hashParams: Params): boolean {
        return this.isMCIRedirect(queryParams)
            || this.isGeorgeRedirect(queryParams, hashParams)
            || this.isCpsRedirect(queryParams)
            || this.isGeorgeOidcRedirect(queryParams, hashParams);
    }

    private isMCIRedirect(queryParams: Params): boolean {
        return !!queryParams.channelId;
    };

    private isGeorgeRedirect(queryParams: Params, hashParams: Params): boolean {
        const georgeHashParams = !!hashParams.access_token && !!hashParams.state;
        return this.queryParamsEmptyOrDebugOnly(queryParams) && georgeHashParams;
    }

    private isGeorgeOidcRedirect(queryParams: Params, hashParams: Params): boolean {
        const georgeHashParams = !!hashParams.state;
        const oidcHashParams = !!hashParams.code && !!hashParams.id_token;
        return this.queryParamsEmptyOrDebugOnly(queryParams) && (georgeHashParams || oidcHashParams) && !hashParams.access_token;
    }

    private isCpsRedirect(queryParams: Params): boolean {
        return !!queryParams.cluid;
    }

    /**
     * Returns true if query parameters are empty, or there is only one parameter - debug.
     */
    private queryParamsEmptyOrDebugOnly(queryParams: Params): boolean {
        const isEmptyQueryParams = Object.getOwnPropertyNames(queryParams).length === 0;
        const isOnlyDebugQueryParam = Object.getOwnPropertyNames(queryParams).length === 1 && !!queryParams['debug'];
        return isEmptyQueryParams || isOnlyDebugQueryParam;
    }

    // -----------------------------------------------------------------------------------------------------------------

    constructor(private store: Store<UrlParamsState>,
                private actions$: Actions,
                private configGuard: ConfigLoadedGuard,
                private auth: AuthenticationService,
                private authOidc: AuthenticationOidcService,
                private logger: LoggerService) {
    }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/modules/url-params/effects/url-params.effects.ts